import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import { useSession } from "../lib/auth-client";
import { useTelemetryBeacon } from "../hooks/use-telemetry";
import { installUsageTelemetry, recordPageView } from "../lib/telemetry-events";
import { returnToPath } from "../lib/auth-return";
import { useIsUnattendedSurface, useReportSessionHold } from "../lib/unattended-session";
import { shouldHoldSignedOutView } from "../lib/wallboard-continuity";
import { sessionCheckFailed, sessionCheckTimedOut } from "../lib/session-check";
import { isKioskDevice } from "../lib/station-client";
import { SESSION_TIMEOUT_MESSAGE } from "../../shared/session-timeout";
import { bindRbacCacheOwner, clearRbacCache } from "../hooks/use-rbac";

/** How often to re-check the session while an unattended screen is held. */
const SILENT_REAUTH_INTERVAL_MS = 60_000;

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data: session, isPending, error, refetch } = useSession();
  const location = useLocation();
  const unattended = useIsUnattendedSurface();
  const hadSession = useRef(false);

  // #4952 round 3: the RBAC warm-load cache is read and written for this
  // session's user only. Bound here, in render, so the children below read it
  // for the same user on their first render; every reader sits inside this.
  bindRbacCacheOwner(session?.user?.id);

  // #919: once-per-session device beacon — fires only for a real signed-in
  // session (fail-silent; see the hook). Hook order is stable: called every
  // render, gated by the enabled flag.
  useTelemetryBeacon(!isPending && !!session?.user);

  // #1876: feature-usage beacon — page_load per route change, batched and
  // fail-silent (see telemetry-events.ts). Signed-in sessions only; the
  // pagehide flush hook installs once.
  const authed = !isPending && !!session?.user;
  useEffect(() => {
    if (!authed) return;
    return installUsageTelemetry();
  }, [authed]);
  useEffect(() => {
    if (!authed) return;
    try {
      recordPageView(location.pathname);
    } catch {
      // Telemetry must never interrupt the application.
    }
  }, [authed, location.pathname]);

  useEffect(() => {
    if (session?.user) hadSession.current = true;
  }, [session]);

  // #1207: an unattended screen (the Production Monitor wallboard) that was
  // signed in and then lost its session keeps its last good render instead of
  // being replaced by the sign-in landing. better-auth nulls its client session
  // on a 401 from /get-session, and that refetch fires on window focus / online /
  // cross-tab broadcast — so a screen wake on an idle board was enough to take
  // the board over. A held view fetches nothing new; only what is already in
  // memory stays on screen, so this grants no unauthenticated data reach.
  const holdSignedOut = shouldHoldSignedOutView({
    hasSession: !!session?.user,
    unattended,
    hadSession: hadSession.current,
  });
  // #4836: tell the held surface, so Display Mode does not reload itself
  // into the sign-in page for a new deploy while its session is gone.
  useReportSessionHold(holdSignedOut);

  // Silent re-auth while held: better-auth's /get-session round trip
  // re-establishes the session (and refreshes the cookie when the server says it
  // needs refreshing), so a lapse recovers with nobody at the screen.
  useEffect(() => {
    if (!holdSignedOut) return;
    void refetch?.();
    const id = setInterval(() => {
      void refetch?.();
    }, SILENT_REAUTH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [holdSignedOut, refetch]);

  // A held board must not flash the full-screen loader either — the session
  // re-check re-enters the pending state on every attempt.
  if (isPending && !holdSignedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  // #4386: the server could not check the session in time (503
  // SESSION_TIMEOUT) or /get-session gave no answer in 8 s. Ask for a fresh
  // sign-in rather than spinning on "Loading..." or calling it a network blip.
  if (!holdSignedOut && sessionCheckTimedOut(!!session?.user, error)) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <SessionTimeoutBanner
        signInHref={returnToPath("/login", returnTo)}
        onRetry={() => void refetch?.()}
      />
    );
  }

  // #1666: a FAILED session check (network / edge 5xx — anything but a clean
  // sign-out or 401) holds the route with a retry instead of redirecting: a
  // signed-in user on flaky wifi must never be silently dumped to /welcome.
  if (!holdSignedOut && sessionCheckFailed(!!session?.user, error)) {
    return <SessionCheckHolding onRetry={() => void refetch?.()} />;
  }

  if (!session?.user && !holdSignedOut) {
    // #4952 round 3: the session ended (idle, the 12 h cap, a revoke) or never
    // was; the next person to sign in on this browser starts cold.
    clearRbacCache();
    // #4447: a screen that has signed in with a PIN before returns to its
    // keypad when the session ends (idle, 12 h cap, revoke), not to the
    // public landing page. The flag is this device's only; /station then
    // reads the truth from the server and clears it when the station is gone.
    if (isKioskDevice()) return <Navigate to="/station" replace />;
    // Send anonymous visitors to the public branded landing page (not straight
    // to the bare /login credential prompt). The landing has the Sign-in CTA.
    // See Landing.tsx + #460 for why the public face is content, not a login.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={returnToPath("/welcome", returnTo)} replace />;
  }

  return <>{children}</>;
}

/** #1666 — the holding state for a failed (not signed-out) session check. */
function SessionCheckHolding({ onRetry }: { onRetry: () => void }) {
  useEffect(() => {
    const id = setInterval(onRetry, 10_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-sm rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 text-center">
        <p className="text-sm font-medium text-foreground">Couldn't confirm your session</p>
        <p className="mt-1 text-xs text-muted-foreground">
          This looks like a connection problem, not a sign-out. Retrying automatically —
          your work and sign-in are untouched.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
        >
          Retry now
        </button>
      </div>
    </div>
  );
}

/** #4386: the session check timed out; offer a fresh sign-in, and a retry. */
export function SessionTimeoutBanner({ signInHref, onRetry }: { signInHref: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div role="alert" className="max-w-sm rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 text-center">
        <p className="text-sm font-medium text-foreground">{SESSION_TIMEOUT_MESSAGE}</p>
        <div className="mt-3 flex items-center justify-center gap-2">
          <a
            href={signInHref}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            Sign in again
          </a>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
